import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { zAddress } from '@ultrasnip/contracts';
import { z } from 'zod';
import { tokenOverviewQuery } from '@/features/token/token-api';
import type { ActivityFilter } from '@/features/token/token-model';
import { TokenMissing, TokenScreen } from '@/features/token/token-screen';

export const Route = createFileRoute('/_app/tokens/$mint')({
  validateSearch: z.object({
    /** The Activity filter: leaders' trades or yours (both, the default, is left out of the URL). */
    activity: z.enum(['leaders', 'you']).optional().catch(undefined),
  }),
  loader: ({ context: { queryClient }, params }) => {
    const mint = zAddress.safeParse(params.mint);
    // Start the first read while the screen mounts; the screen owns loading and error states.
    if (mint.success) void queryClient.prefetchQuery(tokenOverviewQuery(mint.data));
  },
  component: TokenRoute,
});

function TokenRoute() {
  const { mint } = Route.useParams();
  const { activity = 'all' } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const parsed = zAddress.safeParse(mint);
  if (!parsed.success) return <TokenMissing mint={mint} invalid />;
  const setActivity = (next: ActivityFilter) =>
    void navigate({
      search: (prev) => ({ ...prev, activity: next === 'all' ? undefined : next }),
      replace: true,
      resetScroll: false,
    });
  // Keyed by mint: moving between tokens starts each page fresh.
  return <TokenScreen key={parsed.data} mint={parsed.data} activity={activity} onActivity={setActivity} />;
}
